import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { BookOpen, Mail, Loader2 } from "lucide-react";
import { toast } from "sonner";

const PRETTY = {
  "guest-portal": "Guest Portal",
  "property-docs": "Property Guide & Rental Agreement",
};

export default function OnboardingCompletionCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [nudging, setNudging] = useState(null); // slug being nudged
  const [expanded, setExpanded] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/training/onboarding-completion");
      setData(data);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const nudge = async (slug) => {
    const g = (data?.guides || []).find((x) => x.slug === slug);
    const count = g?.stragglers?.length || 0;
    if (count === 0) {
      toast.success("Everyone's done — nothing to nudge");
      return;
    }
    if (!window.confirm(
      `Email ${count} staff member${count === 1 ? "" : "s"} who haven't downloaded the ${PRETTY[slug] || slug} guide?`
    )) return;
    setNudging(slug);
    try {
      const { data: res } = await api.post("/training/nudge-stragglers", { slug });
      toast.success(`Nudged ${res.sent} of ${res.stragglers} straggler${res.stragglers === 1 ? "" : "s"}`);
      load();
    } catch (err) {
      toast.error(apiError(err, "Nudge failed"));
    } finally {
      setNudging(null);
    }
  };

  if (!data) return null;

  return (
    <section
      data-testid="dashboard-onboarding-completion"
      className="ns-card p-6"
    >
      <div className="flex items-start justify-between gap-4 mb-5">
        <div className="min-w-0">
          <div className="ns-label">Team onboarding</div>
          <div className="font-serif text-xl mt-1">Training guide completion</div>
          <div className="text-xs text-slate-400 mt-1">
            {data.staff_total} staff member{data.staff_total === 1 ? "" : "s"} with email · nudge the ones who haven't downloaded yet
          </div>
        </div>
        <div className="w-9 h-9 rounded-full bg-teal-50 text-teal-700 flex items-center justify-center">
          <BookOpen className="w-4 h-4" />
        </div>
      </div>

      <ul className="space-y-3">
        {loading ? (
          <div className="text-xs text-slate-400 italic py-4">Loading…</div>
        ) : (
          (data.guides || []).map((g) => {
            const pct = g.percent_complete;
            const stragglers = g.stragglers || [];
            const barColor = pct >= 80
              ? "bg-emerald-500"
              : pct >= 50 ? "bg-amber-500" : "bg-red-500";
            return (
              <li
                key={g.slug}
                data-testid={`onboarding-row-${g.slug}`}
                className="rounded-lg border border-slate-100 bg-white p-4"
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-slate-800 truncate">
                      {PRETTY[g.slug] || g.slug}
                    </div>
                    <div className="text-[11px] text-slate-500 mt-0.5">
                      {g.staff_downloaded} of {g.staff_total} downloaded
                    </div>
                  </div>
                  <div className="font-serif text-2xl text-slate-900 tabular-nums shrink-0">
                    {pct}%
                  </div>
                </div>
                <div className="mt-3 h-1.5 w-full bg-stone-100 rounded-full overflow-hidden">
                  <div
                    className={`h-full ${barColor} transition-all`}
                    style={{ width: `${pct}%` }}
                    data-testid={`onboarding-bar-${g.slug}`}
                  />
                </div>
                {stragglers.length > 0 && (
                  <div className="mt-3 flex items-center gap-2 flex-wrap">
                    <button
                      type="button"
                      data-testid={`onboarding-nudge-${g.slug}`}
                      onClick={() => nudge(g.slug)}
                      disabled={nudging === g.slug}
                      className="inline-flex items-center gap-1.5 rounded-full border border-teal-200 text-teal-700 bg-teal-50/60 hover:bg-teal-100 text-[11px] px-3 py-1 transition"
                    >
                      {nudging === g.slug
                        ? <Loader2 className="w-3 h-3 animate-spin" />
                        : <Mail className="w-3 h-3" />}
                      Nudge {stragglers.length} straggler{stragglers.length === 1 ? "" : "s"}
                    </button>
                    <button
                      type="button"
                      data-testid={`onboarding-expand-${g.slug}`}
                      onClick={() => setExpanded(expanded === g.slug ? null : g.slug)}
                      className="text-[11px] text-slate-500 hover:text-teal-700 underline"
                    >
                      {expanded === g.slug ? "Hide" : "See who"}
                    </button>
                  </div>
                )}
                {expanded === g.slug && stragglers.length > 0 && (
                  <ul
                    data-testid={`onboarding-stragglers-${g.slug}`}
                    className="mt-3 bg-stone-50/60 border border-slate-100 rounded-lg divide-y divide-slate-100 text-xs"
                  >
                    {stragglers.slice(0, 10).map((s) => (
                      <li key={s.id} className="px-3 py-2 flex items-center justify-between gap-2">
                        <div className="min-w-0">
                          <div className="text-slate-700 truncate">{s.name || s.email}</div>
                          <div className="text-[10px] text-slate-400">{s.role} · {s.email}</div>
                        </div>
                      </li>
                    ))}
                    {stragglers.length > 10 && (
                      <li className="px-3 py-2 text-[10px] text-slate-400 italic">
                        …and {stragglers.length - 10} more
                      </li>
                    )}
                  </ul>
                )}
              </li>
            );
          })
        )}
      </ul>
    </section>
  );
}
